import React from "react";

export interface CheckInTrackingContext {
  step: number;
  page: string;
}

interface CheckInEventOptions {
  action: string;
  valid?: boolean;
  choice?: string;
  insuranceIndex?: number;
  [key: string]: unknown;
}

export const buildCheckInEventName = (
  step: number,
  action: string
): string =>
  /^step_\d+_dci_/.test(action) ? action : `step_${step}_dci_${action}`;

export const pushCheckInEvent = (
  trackingContext: CheckInTrackingContext,
  { action, valid, choice, insuranceIndex, ...eventProperties }: CheckInEventOptions
): void => {
  const event = {
    event: buildCheckInEventName(trackingContext.step, action),
    page: trackingContext.page,
    ...(valid ? { valid } : {}),
    ...(choice ? { choice } : {}),
    ...(insuranceIndex ? { insurance_index: insuranceIndex } : {}),
    ...eventProperties,
  };

  window.dataLayer = window.dataLayer || [];
  console.log("Tracking event:", event);
  window.dataLayer.push(event);
};

export const useCheckInTracking = (
  trackingContext: CheckInTrackingContext
): {
  trackCheckInEvent: (
    action: string,
    options?: Omit<CheckInEventOptions, "action">
  ) => void;
} => {
  const { step, page } = trackingContext;
  const trackCheckInEvent = React.useCallback(
    (
      action: string,
      options: Omit<CheckInEventOptions, "action"> = {}
    ): void => {
      pushCheckInEvent({ step, page }, { action, ...options });
    },
    [page, step]
  );

  return { trackCheckInEvent };
};

export const useCheckInPageTracking = (
  trackingContext: CheckInTrackingContext
): void => {
  const { trackCheckInEvent } = useCheckInTracking(trackingContext);

  React.useEffect(
    () => {
      trackCheckInEvent("land");
    },
    /* eslint-disable react-hooks/exhaustive-deps */
    []
    /* eslint-enable react-hooks/exhaustive-deps */
  );
};

export const useCheckInNavigationTracking = (
  trackingContext: CheckInTrackingContext,
  completedRef?: React.MutableRefObject<boolean>
): void => {
  const { trackCheckInEvent } = useCheckInTracking(trackingContext);

  React.useEffect(() => {
    const handlePopState = () => {
      trackCheckInEvent("back");
    };
    const handleBeforeUnload = () => {
      if (!completedRef?.current) {
        trackCheckInEvent("exit");
      }
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [completedRef, trackCheckInEvent]);
};

interface UseCheckInValidFieldTrackingProps extends CheckInTrackingContext {
  action: string;
  isValid: boolean;
  choice?: string;
  insuranceIndex?: number;
}

export const useCheckInValidFieldTracking = ({
  step,
  page,
  action,
  isValid,
  choice,
  insuranceIndex,
}: UseCheckInValidFieldTrackingProps): void => {
  const { trackCheckInEvent } = useCheckInTracking({ step, page });
  const trackedEventsRef = React.useRef<Set<string>>(new Set());
  const eventKey = `${action}-${insuranceIndex || 1}`;

  React.useEffect(() => {
    if (!isValid || trackedEventsRef.current.has(eventKey)) {
      return;
    }

    trackCheckInEvent(action, {
      valid: true,
      ...(choice ? { choice } : {}),
      ...(insuranceIndex ? { insuranceIndex } : {}),
    });
    trackedEventsRef.current.add(eventKey);
  }, [
    action,
    choice,
    eventKey,
    insuranceIndex,
    isValid,
    trackCheckInEvent,
  ]);
};
